/**
 * Centralized Error Logging and Monitoring Service
 *
 * Adapted for Next.js with SSR guards.
 * Provides a unified interface for error logging, monitoring, and reporting.
 */

const isBrowser = typeof window !== "undefined";

export enum ErrorSeverity {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
  CRITICAL = "critical",
}

export enum ErrorCategory {
  NETWORK = "network",
  AUTHENTICATION = "authentication",
  AUTHORIZATION = "authorization",
  VALIDATION = "validation",
  BUSINESS_LOGIC = "business_logic",
  RUNTIME = "runtime",
  WEBSOCKET = "websocket",
  SIGNALR = "signalr",
  UNKNOWN = "unknown",
}

export interface ErrorContext {
  userId?: string;
  userEmail?: string;
  url?: string;
  component?: string;
  action?: string;
  requestId?: string;
  timestamp?: number;
  method?: string;
  endpoint?: string;
  attempt?: number;
  reconnectAttempts?: number;
  maxAttempts?: number;
  delay?: number;
  additionalData?: Record<string, unknown>;
}

export interface ErrorLog {
  id: string;
  message: string;
  severity: ErrorSeverity;
  category: ErrorCategory;
  error?: Error;
  context?: ErrorContext;
  timestamp: number;
  stackTrace?: string;
}

export interface ErrorRecoveryHint {
  category: ErrorCategory;
  statusCode?: number;
  message: string;
  actions: string[];
}

class ErrorLoggerService {
  private errorQueue: ErrorLog[] = [];
  private maxQueueSize = 100;
  private isOnline = isBrowser ? navigator.onLine : true;
  private listeners: Set<(error: ErrorLog) => void> = new Set();
  private initialized = false;

  constructor() {
    if (isBrowser) {
      this.setupNetworkListeners();
    }
  }

  private setupNetworkListeners(): void {
    if (!isBrowser || this.initialized) return;
    this.initialized = true;

    window.addEventListener("online", () => {
      this.isOnline = true;
      console.log("Network online - flushing error queue");
      this.flushErrorQueue();
    });

    window.addEventListener("offline", () => {
      this.isOnline = false;
      console.warn("Network offline - errors will be queued");
    });
  }

  logError(
    message: string,
    error?: Error | unknown,
    severity: ErrorSeverity = ErrorSeverity.MEDIUM,
    category: ErrorCategory = ErrorCategory.UNKNOWN,
    context?: ErrorContext
  ): ErrorLog {
    const errorLog: ErrorLog = {
      id: this.generateErrorId(),
      message,
      severity,
      category,
      error: error instanceof Error ? error : undefined,
      context: {
        ...context,
        timestamp: context?.timestamp || Date.now(),
        url: context?.url || (isBrowser ? window.location.href : ""),
      },
      timestamp: Date.now(),
      stackTrace: error instanceof Error ? error.stack : undefined,
    };

    this.addToQueue(errorLog);
    this.logToConsole(errorLog);
    this.notifyListeners(errorLog);

    if (this.isOnline) {
      this.sendToExternalService(errorLog);
    }

    return errorLog;
  }

  logNetworkError(
    message: string,
    statusCode?: number,
    context?: ErrorContext
  ): ErrorLog {
    const severity = this.getNetworkErrorSeverity(statusCode);
    return this.logError(
      message,
      undefined,
      severity,
      ErrorCategory.NETWORK,
      { ...context, additionalData: { statusCode } }
    );
  }

  logAuthError(message: string, context?: ErrorContext): ErrorLog {
    return this.logError(
      message,
      undefined,
      ErrorSeverity.HIGH,
      ErrorCategory.AUTHENTICATION,
      context
    );
  }

  logAuthorizationError(message: string, context?: ErrorContext): ErrorLog {
    return this.logError(
      message,
      undefined,
      ErrorSeverity.MEDIUM,
      ErrorCategory.AUTHORIZATION,
      context
    );
  }

  logValidationError(message: string, context?: ErrorContext): ErrorLog {
    return this.logError(
      message,
      undefined,
      ErrorSeverity.LOW,
      ErrorCategory.VALIDATION,
      context
    );
  }

  logWebSocketError(
    message: string,
    error?: Error,
    context?: ErrorContext
  ): ErrorLog {
    return this.logError(
      message,
      error,
      ErrorSeverity.MEDIUM,
      ErrorCategory.WEBSOCKET,
      context
    );
  }

  logSignalRError(
    message: string,
    error?: Error,
    context?: ErrorContext
  ): ErrorLog {
    return this.logError(
      message,
      error,
      ErrorSeverity.MEDIUM,
      ErrorCategory.SIGNALR,
      context
    );
  }

  getRecoveryHint(
    category: ErrorCategory,
    statusCode?: number
  ): ErrorRecoveryHint {
    const hints: ErrorRecoveryHint[] = [
      {
        category: ErrorCategory.NETWORK,
        statusCode: 0,
        message: "No internet connection",
        actions: [
          "Check your internet connection",
          "Try again in a few moments",
          "Changes will be synced when connection is restored",
        ],
      },
      {
        category: ErrorCategory.NETWORK,
        statusCode: 500,
        message: "Server error",
        actions: [
          "Try again in a few moments",
          "If the problem persists, contact support",
          "Your data has been saved locally",
        ],
      },
      {
        category: ErrorCategory.NETWORK,
        statusCode: 503,
        message: "Service temporarily unavailable",
        actions: [
          "The system is under maintenance",
          "Try again in a few minutes",
          "Contact support if needed",
        ],
      },
      {
        category: ErrorCategory.AUTHENTICATION,
        message: "Authentication error",
        actions: [
          "Your session has expired",
          "Please log in again",
          "Check your credentials",
        ],
      },
      {
        category: ErrorCategory.AUTHORIZATION,
        statusCode: 403,
        message: "Access denied",
        actions: [
          "You don't have permission for this action",
          "Contact your administrator",
          "Check if your user profile is up to date",
        ],
      },
      {
        category: ErrorCategory.VALIDATION,
        message: "Validation error",
        actions: [
          "Check the entered data",
          "Fix the fields highlighted in red",
          "Make sure all required fields are filled",
        ],
      },
      {
        category: ErrorCategory.SIGNALR,
        message: "Real-time connection error",
        actions: [
          "The real-time connection was lost",
          "Attempting to reconnect automatically...",
          "Refresh the page if the problem persists",
        ],
      },
    ];

    const hint =
      hints.find(
        (h) =>
          h.category === category && (!statusCode || h.statusCode === statusCode)
      ) || hints.find((h) => h.category === category);

    return (
      hint || {
        category: ErrorCategory.UNKNOWN,
        message: "An unexpected error occurred",
        actions: [
          "Try again",
          "Refresh the page",
          "Contact support if the problem persists",
        ],
      }
    );
  }

  addListener(listener: (error: ErrorLog) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getErrorHistory(limit = 50): ErrorLog[] {
    return this.errorQueue.slice(-limit);
  }

  clearHistory(): void {
    this.errorQueue = [];
  }

  getStatistics(): {
    total: number;
    bySeverity: Record<ErrorSeverity, number>;
    byCategory: Record<ErrorCategory, number>;
  } {
    const stats = {
      total: this.errorQueue.length,
      bySeverity: {} as Record<ErrorSeverity, number>,
      byCategory: {} as Record<ErrorCategory, number>,
    };

    this.errorQueue.forEach((error) => {
      stats.bySeverity[error.severity] =
        (stats.bySeverity[error.severity] || 0) + 1;
      stats.byCategory[error.category] =
        (stats.byCategory[error.category] || 0) + 1;
    });

    return stats;
  }

  private generateErrorId(): string {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private addToQueue(error: ErrorLog): void {
    this.errorQueue.push(error);
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue.shift();
    }
  }

  private logToConsole(error: ErrorLog): void {
    const severityEmoji = {
      [ErrorSeverity.LOW]: "[INFO]",
      [ErrorSeverity.MEDIUM]: "[WARN]",
      [ErrorSeverity.HIGH]: "[ERROR]",
      [ErrorSeverity.CRITICAL]: "[CRITICAL]",
    };

    const prefix = `${severityEmoji[error.severity]} [${error.category.toUpperCase()}]`;

    if (
      error.severity === ErrorSeverity.CRITICAL ||
      error.severity === ErrorSeverity.HIGH
    ) {
      console.error(prefix, error.message, error);
    } else if (error.severity === ErrorSeverity.MEDIUM) {
      console.warn(prefix, error.message, error);
    } else {
      console.log(prefix, error.message, error);
    }
  }

  private notifyListeners(error: ErrorLog): void {
    this.listeners.forEach((listener) => {
      try {
        listener(error);
      } catch (err) {
        console.error("Error in error listener:", err);
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private async sendToExternalService(_error: ErrorLog): Promise<void> {
    // TODO: Integrate with external services like Application Insights, Sentry, etc.
  }

  private async flushErrorQueue(): Promise<void> {
    if (this.errorQueue.length === 0) return;

    console.log(`Flushing ${this.errorQueue.length} queued errors`);

    const errors = [...this.errorQueue];
    for (const error of errors) {
      await this.sendToExternalService(error);
    }
  }

  private getNetworkErrorSeverity(statusCode?: number): ErrorSeverity {
    if (!statusCode) return ErrorSeverity.HIGH;
    if (statusCode >= 500) return ErrorSeverity.HIGH;
    if (statusCode >= 400) return ErrorSeverity.MEDIUM;
    return ErrorSeverity.LOW;
  }
}

// Singleton instance
export const errorLogger = new ErrorLoggerService();
